import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import api from '../services/api';
import { ACCOUNT_TYPE_LABELS, STATUS_LABELS } from '../constants/accountStatus';
import { apiError, formatDate, formatDateTimeMsk, formatMoney, pluralize } from '../utils/format';
import { marketplaceMeta, tokens } from '../theme';

// What the administrator did / what happened, as written in the journal.
const EVENT_LABELS = {
  access_given: 'Выдан доступ',
  access_revoked: 'Забран доступ',
  blocked: 'Заблокирован',
  settings_changed: 'Изменены настройки',
  email_verified: 'Почта подтверждена',
  password_changed: 'Сменён пароль',
  account_added: 'Подключён кабинет',
  account_deleted: 'Удалён кабинет',
  account_replaced: 'Заменён ключ кабинета',
  account_renamed: 'Переименован кабинет',
};

const REQUEST_STATUS = { processed: 'Оплачена', new: 'Не оплачена', cancelled: 'Отменена' };
const AGENT_STATUS = { pending: 'Заявка на рассмотрении', active: 'Агент', blocked: 'Заблокирован', rejected: 'Заявка отклонена', deleted: 'Удалён' };
const EMPLOYEE_ROLE = { manager: 'Менеджер', accountant: 'Бухгалтер', financier: 'Финансист' };

const dateTime = (v) => (v ? formatDateTimeMsk(v) : '—');
const day = (v) => (v ? formatDate(v) : '—');

const Section = ({ title, note, children }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
    {note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{note}</Typography>}
    {children}
  </Box>
);

const Field = ({ label, children }) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{children}</Typography>
  </Stack>
);

// The card of one person for the administrator. Read-only; nothing secret is shown.
const UserDetailsDialog = ({ userId, onClose }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setD(null);
    setError('');
    api.get(`/admin/users/${userId}/details`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => { if (!cancelled) setError(apiError(e)); });
    return () => { cancelled = true; };
  }, [userId]);

  const a = d?.access;
  const daysLeft = a?.days_left;
  const noJournalYet = d && d.events.length === 0;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {d ? d.email : 'Пользователь'}
        {d && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={d.role === 'admin' ? 'Администратор' : (ACCOUNT_TYPE_LABELS[d.account_type] || '—')} />
            {d.type_manual && <Chip size="small" variant="outlined" label="тип задан вручную" />}
            <Chip size="small" label={STATUS_LABELS[d.status] || d.status} />
            {a?.is_trial && <Chip size="small" label="Пробный" sx={{ bgcolor: tokens.infoSoft, color: tokens.info }} />}
            {d.trial_lapsed && <Chip size="small" label="Пробный закончился, не купил" sx={{ bgcolor: tokens.warningSoft, color: tokens.warning }} />}
          </Stack>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!d && !error && <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>}
        {d && (
          <Stack spacing={2.5} divider={<Divider flexItem />}>
            <Section title="Основное">
              <Field label="Имя">{d.full_name || '—'}</Field>
              <Field label="Зарегистрирован">{dateTime(d.created_at)}</Field>
              <Field label="Почта подтверждена">{d.email_verified_at ? dateTime(d.email_verified_at) : 'нет'}</Field>
              <Field label="Согласие с офертой">{d.terms_accepted_at ? `${dateTime(d.terms_accepted_at)}${d.legal_version ? ` · редакция ${d.legal_version}` : ''}` : '—'}</Field>
              <Field label="Согласие на обработку ПДн">{d.pd_consent_at ? dateTime(d.pd_consent_at) : '—'}</Field>
            </Section>

            <Section title="Активность" note="Входы записываются с момента запуска этой функции: более ранних данных нет.">
              <Field label="Последний вход">{dateTime(d.activity.last_login_at)}</Field>
              <Field label="Последняя активность">{dateTime(d.activity.last_seen_at)}</Field>
              <Field label="Входов">{d.activity.login_count || '—'}</Field>
            </Section>

            <Section title="Доступ">
              <Field label="Состояние">{STATUS_LABELS[d.status] || d.status}{a.is_trial ? ' · пробный' : ''}</Field>
              <Field label="Первый доступ">{a.activated_at ? dateTime(a.activated_at) : 'не выдавался'}</Field>
              <Field label="Действует до">{a.expires_at ? day(a.expires_at) : (a.activated_at ? 'без ограничения срока' : '—')}</Field>
              {daysLeft != null && <Field label="Осталось">{daysLeft > 0 ? `${daysLeft} ${pluralize(daysLeft, 'день', 'дня', 'дней')}` : 'срок истёк'}</Field>}
              <Field label="Пробный период">{a.trial_used_at ? `использован ${day(a.trial_used_at)}` : 'не использовался'}</Field>
              <Field label="Покупал тариф">{a.ever_paid ? 'да' : 'нет'}</Field>
            </Section>

            <Section title="Платежи">
              <Field label="Оплат всего">{d.billing.paid_count ? `${d.billing.paid_count} на ${formatMoney(d.billing.paid_total_rub)}` : 'не было'}</Field>
              <Field label="Первая оплата">{day(d.billing.first_paid_at)}</Field>
              <Field label="Последняя оплата">{day(d.billing.last_paid_at)}</Field>
              {d.billing.requests.length > 0 && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow><TableCell>Заявка</TableCell><TableCell>Срок</TableCell><TableCell align="right">Сумма</TableCell><TableCell>Статус</TableCell><TableCell>Оплачена</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {d.billing.requests.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{day(p.created_at)}{p.promo_code ? ` · промокод ${p.promo_code}` : ''}</TableCell>
                        <TableCell>{p.months} мес.</TableCell>
                        <TableCell align="right">{formatMoney(p.amount_rub)}</TableCell>
                        <TableCell>{REQUEST_STATUS[p.status] || p.status}</TableCell>
                        <TableCell>{p.paid_at ? `${day(p.paid_at)}${p.online ? ' · онлайн' : ' · по счёту'}` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Section>

            <Section title="Кабинеты" note="Подключения и замены записываются с момента запуска журнала: что было раньше, неизвестно.">
              {d.accounts.current.length === 0 ? <Typography variant="body2">Сейчас кабинетов нет</Typography> : (
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Площадка</TableCell><TableCell>Название</TableCell><TableCell>Подключён</TableCell><TableCell>Загрузка данных</TableCell><TableCell>Последняя синхронизация</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {d.accounts.current.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{marketplaceMeta(c.marketplace).label}</TableCell>
                        <TableCell>{c.name || '—'}</TableCell>
                        <TableCell>{day(c.connected_at)}</TableCell>
                        <TableCell>
                          {c.sync_sources ? `${c.sync_ready} из ${c.sync_sources} готово` : 'ещё не начата'}
                          {c.sync_errors > 0 && <Typography component="span" variant="caption" sx={{ color: tokens.error }}> · ошибок: {c.sync_errors}</Typography>}
                        </TableCell>
                        <TableCell>{dateTime(c.last_sync_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Field label="По журналу">подключено раз: {d.accounts.added} · удалено: {d.accounts.removed} · ключ заменён: {d.accounts.replaced}</Field>
            </Section>

            <Section title="Команда">
              {d.team.owner_email && <Field label="Сотрудник у">{d.team.owner_email}</Field>}
              {d.team.employees.length === 0 && !d.team.owner_email && <Typography variant="body2">Сотрудников нет</Typography>}
              {d.team.employees.map((e, i) => <Field key={i} label={EMPLOYEE_ROLE[e.role] || e.role}>{e.email} · с {day(e.created_at)}</Field>)}
            </Section>

            <Section title="Партнёрская программа">
              <Field label="Заявка агента">{d.referral.agent_status ? `${AGENT_STATUS[d.referral.agent_status] || d.referral.agent_status} · код ${d.referral.agent_code} · подана ${day(d.referral.applied_at)}` : 'не подавал'}</Field>
              <Field label="Пришёл по ссылке">
                {d.referral.came_via ? `агента ${d.referral.came_via.agent_name} (${d.referral.came_via.agent_code})${d.referral.came_via.clicked_at ? `, переход ${day(d.referral.came_via.clicked_at)}` : ''}` : 'нет'}
              </Field>
            </Section>

            <Section title="Журнал действий" note="Что делали с аккаунтом администраторы и что происходило с кабинетами. Ведётся с момента запуска журнала.">
              {noJournalYet && <Typography variant="body2">Записей пока нет</Typography>}
              {d.events.map((e, i) => (
                <Box key={i} sx={{ py: 0.4 }}>
                  <Typography variant="body2"><b>{EVENT_LABELS[e.kind] || e.kind}</b>{e.details ? ` · ${e.details}` : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{dateTime(e.created_at)}{e.actor ? ` · ${e.actor}` : ''}</Typography>
                </Box>
              ))}
            </Section>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
